/**
 * `useDataTable` — owns the TanStack Table v8 + react-virtual engine so no call
 * site imports `@tanstack/*` directly (`no-restricted-imports` boundary).
 *
 * Stable-reference contract (TanStack #5567): under React 19 + the React
 * Compiler, TanStack re-renders wrong when `data`/`columns` change identity
 * every render. Pass a stable `data`, OR a stable `source` + `flatten` (+
 * optional `dedupeBy`) which the wrapper memoizes on `source` — memoize on the
 * stable input, never on the fresh transform output. `flatten`, `dedupeBy`,
 * `getRowId`, and `virtual.estimateSize` run during render, so they too must be
 * reference-stable; `useEvent` can't wrap them (it throws when called in render)
 * and is reserved for the event-time `onSortingChange`. Stable identities use
 * `useMemo`/module scope/`useEvent`, never `useCallback`; the
 * `preserve-manual-memoization` rule is off repo-wide (FE-437) so these memos
 * survive — do NOT add an inline eslint-disable here.
 */
import { type RefObject, useMemo, useRef, useState } from 'react'
import { useEvent } from 'react-use-event-hook'
import {
  type ColumnDef,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnResizeMode,
  type ColumnSizingInfoState,
  type ColumnSizingState,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type Row,
  type RowData,
  type RowModel,
  type SortingState,
  type Table,
  type TableOptions,
  type TableState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table'
import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual'

import {
  DATA_TABLE_ROW_GAP_PX,
  DATA_TABLE_ROW_HEIGHT,
  type DataTableDensity,
  DEFAULT_DATA_TABLE_DENSITY,
} from './data-table.styles'

/**
 * Identity helper — returns `columns` unchanged so `createColumns<Row>([...])`
 * infers every `accessorKey`/`cell` against `Row`, and sites import `ColumnDef`
 * from the DS boundary, not `@tanstack/react-table`.
 */
export const createColumns = <TData extends RowData>(
  columns: ColumnDef<TData>[],
): ColumnDef<TData>[] => columns

const DEFAULT_OVERSCAN = 10

/** Stable empty array so the no-data path keeps a stable reference. */
const EMPTY_ROWS: never[] = []

// Uncontrolled-path seed values for the interactive engine-state slices. Empty
// sorting/pinning/sizing/order all mean "TanStack defaults" (unsorted, nothing pinned, def
// widths, def order), so seeding them never changes an unconfigured table's render.
const EMPTY_SORTING: SortingState = []
const EMPTY_COLUMN_PINNING: ColumnPinningState = { left: [], right: [] }
const EMPTY_COLUMN_SIZING: ColumnSizingState = {}
const EMPTY_COLUMN_ORDER: ColumnOrderState = []
const DEFAULT_COLUMN_SIZING_INFO: ColumnSizingInfoState = {
  startOffset: null,
  startSize: null,
  deltaOffset: null,
  deltaPercentage: null,
  isResizingColumn: false,
  columnSizingStart: [],
}

/**
 * One interactive engine-state slice (sort / pin / size / order) with a
 * controlled/uncontrolled seam. `table.setColumnX` is a no-op unless the matching
 * `onColumnXChange` is wired, so an uncontrolled table would render an inert affordance —
 * the wrapper owns the slice internally on that path so the menu / resize / reorder work.
 *
 * The controlled/uncontrolled line is drawn on the **handler**, not the value: a `state.X`
 * value passed WITHOUT its `onChange` is an INITIAL value the wrapper then owns (uncontrolled,
 * seeded from it), NOT a frozen read-only state that silently deadens the affordance. Only a
 * wired `onChange` (or `forceControlled`, e.g. `manualSorting`) hands ongoing ownership to the
 * consumer. This is the React "value + defaultValue" contract, and it closes the footgun where
 * `columnPinning={{left:['x']}}` alone made "Pin to left" a silent no-op.
 *
 * `useState`/`useEvent` are called unconditionally at a fixed position, so this hook is safe
 * to call once per slice at the top of `useDataTable`.
 */
const useEngineStateSlice = <T>(
  controlledValue: T | undefined,
  onChange: OnChangeFn<T> | undefined,
  seedValue: T,
  forceControlled = false,
): { isControlled: boolean; internalValue: T; engineOnChange: OnChangeFn<T> | undefined } => {
  // Uncontrolled seed is the passed value (an initial value) when present, else the default.
  const [internalValue, setInternalValue] = useState<T>(controlledValue ?? seedValue)
  // Event-time only; a call site may pass an inline handler.
  const handleChange = useEvent<OnChangeFn<T>>(onChange ?? (() => undefined))
  const isControlled = forceControlled || onChange != null
  // Controlled → consumer's handler (undefined only under `forceControlled` without a handler,
  // e.g. `manualSorting` server mode); uncontrolled → the wrapper's setter.
  const controlledOnChange = onChange == null ? undefined : handleChange
  const engineOnChange = isControlled ? controlledOnChange : setInternalValue

  return { isControlled, internalValue, engineOnChange }
}

/** Keep first occurrence per key — the wrapper-owned dedup for the stable-ref contract. */
const dedupeRows = <TData>(rows: TData[], getKey: (row: TData) => string): TData[] => {
  const seen = new Set<string>()
  const result: TData[] = []

  for (const row of rows) {
    const key = getKey(row)
    if (!seen.has(key)) {
      seen.add(key)
      result.push(row)
    }
  }

  return result
}

/**
 * TanStack's row-model factories are runtime-identical across row types (the
 * generic only shapes types), so one module-scope instance avoids per-render
 * realloc; the assertion at the `useReactTable` boundary re-labels the erased
 * `TData` — a pure type relabel, not `as any`.
 */
type RowModelFactory<TData extends RowData> = (table: Table<TData>) => () => RowModel<TData>

const coreRowModel = getCoreRowModel<RowData>()
const sortedRowModel = getSortedRowModel<RowData>()

export type DataTableVirtualOptions = {
  /** Virtualizer row count; defaults to the loaded row count (override for a server total). */
  count?: number
  estimateSize?: (index: number) => number
  overscan?: number
  /** Defaults to the internal `scrollRef` returned by the hook. */
  getScrollElement?: () => HTMLElement | null
  /**
   * Offset (px) of the virtualized body from the scroll container top (the sticky
   * header height). The virtualizer's max-scroll cap is `getTotalSize +
   * scrollMargin - viewport` and `getTotalSize` excludes the header, so without
   * this the last rows fall `scrollMargin` px short. Defaults to `0`. When
   * non-zero the render MUST offset each row by the same amount
   * (`translateY(item.start - scrollMargin)`), since `item.start` bakes the margin
   * in but the row already sits below that header in flow.
   */
  scrollMargin?: number
  /**
   * Subtracted when aligning a row to the viewport start so `scrollToIndex` lands
   * it BELOW the sticky header, not behind it; set to the header height alongside
   * `scrollMargin`. Defaults to `0`.
   */
  scrollPaddingStart?: number
}

export type UseDataTableOptions<TData extends RowData, TSource = unknown> = {
  /** Pre-computed rows. MUST be reference-stable per render (see module doc). */
  data?: TData[]
  /** Reference-stable upstream the wrapper flattens+dedups (preferred). */
  source?: TSource
  flatten?: (source: TSource) => TData[]
  dedupeBy?: (row: TData) => string
  columns: ColumnDef<TData>[]
  getRowId?: (row: TData, index: number, parent?: Row<TData>) => string
  state?: Partial<TableState>
  onSortingChange?: OnChangeFn<SortingState>
  enableSorting?: boolean
  manualSorting?: boolean
  /**
   * Interactive column-state change handlers. Pass one to lift that slice out
   * (controlled); omit it and the hook owns the slice internally so the in-grid
   * affordance (menu pin, resize handle, reorder drag) still works uncontrolled.
   */
  onColumnPinningChange?: OnChangeFn<ColumnPinningState>
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>
  /** Turn on the engine's column resizing (`getCanResize()`); pairs with the resize handle UI. */
  enableColumnResizing?: boolean
  /** `'onChange'` (live) or `'onEnd'`; only meaningful with `enableColumnResizing`. Defaults to `'onChange'`. */
  columnResizeMode?: ColumnResizeMode
  /** Turn on the engine's column pinning (`getCanPin()`); pairs with the header menu's Pin items. OFF by default. */
  enableColumnPinning?: boolean
  density?: DataTableDensity
  /**
   * Inter-row gap (px) added to each virtualized slot on top of the density row height.
   * Defaults to `DATA_TABLE_ROW_GAP_PX` (the 1px gap between floating rounded rows). The
   * bordered-row preset passes `0` so divider rows sit contiguous and the windowed slot
   * never leaves a gap below the divider line — keeping the slot height and the row's
   * inline height in lockstep for either row model.
   */
  rowGapPx?: number
  virtual?: DataTableVirtualOptions
  /** Extra `useReactTable` options merged last — enable an engine feature the wrapper doesn't surface without forking. */
  tableOptions?: Partial<TableOptions<TData>>
}

export type UseDataTableReturn<TData extends RowData> = {
  /** The live TanStack table instance — the escape hatch. */
  table: Table<TData>
  virtualizer: Virtualizer<HTMLElement, Element>
  rows: Row<TData>[]
  /** Attach to the scroll container / virtualization root (`TableRoot`). */
  scrollRef: RefObject<HTMLDivElement | null>
  density: DataTableDensity
  /**
   * The engine's current `SortingState` as a reactive VALUE. The preset reads
   * this to key its header memo on the sort (deriving each column's direction via
   * `resolveSortDirection`), rather than calling the mutable
   * `column.getIsSorted()` during render — which the React Compiler cannot see
   * change off the stable `table`, freezing the sort indicator. This file is a
   * `.ts` module (not compiler-transformed), so the read here is plain.
   */
  sortingState: SortingState
  /**
   * The engine's current `ColumnSizingState` as a reactive VALUE — the sizing
   * analog of `sortingState`. The preset keys each header cell's rendered width on
   * this (via `resolveColumnWidth`) instead of calling the mutable
   * `header.getSize()` inside the compiler-memoized header cell — which the React
   * Compiler cannot see change off the stable `table`/`header`, so a live resize
   * would freeze the header width while the body cells (widths computed in the
   * preset's own render) track. Same `.ts`-module rationale as `sortingState`.
   */
  columnSizingState: ColumnSizingState
  /**
   * Whether a resize drag is live on ANY column, as a reactive BOOLEAN — the
   * transient-drag companion to `columnSizingState`, read from
   * `columnSizingInfo.isResizingColumn`. The header cells suppress their hover-reveal
   * affordances while it is true, and they need a PROP to do it: the compiler-memoized
   * cell cannot see `table.getState()` change off the stable `table` (same rationale as
   * `sortingState` / `columnSizingState`). It also gives every cell a prop that flips at
   * both edges of the drag, which is what lets the resizing column's own separator read
   * `getIsResizing()` and clear when the drag ends.
   */
  isAnyColumnResizing: boolean
  /**
   * Pinning / order / visibility as reactive VALUES (same `.ts`-module rationale as
   * `sortingState`): together with `columnSizingState` they are exactly the inputs the
   * pinned-offset geometry derives from, so the preset can memoize `pinnedOffsets` on
   * them — a stable-identity map lets the compiler bail out every mounted body row on
   * renders that change none of the pin geometry (e.g. a selection toggle).
   */
  columnPinningState: ColumnPinningState
  columnOrderState: ColumnOrderState
  columnVisibilityState: VisibilityState
}

export const useDataTable = <TData extends RowData, TSource = unknown>(
  options: UseDataTableOptions<TData, TSource>,
): UseDataTableReturn<TData> => {
  const {
    data,
    source,
    flatten,
    dedupeBy,
    columns,
    getRowId,
    state,
    onSortingChange,
    enableSorting,
    manualSorting,
    onColumnPinningChange,
    onColumnSizingChange,
    onColumnOrderChange,
    enableColumnResizing,
    columnResizeMode = 'onChange',
    enableColumnPinning,
    density = DEFAULT_DATA_TABLE_DENSITY,
    rowGapPx = DATA_TABLE_ROW_GAP_PX,
    virtual,
    tableOptions,
  } = options

  // Memoized on the stable upstream: `flatten`/`dedupeBy` run in render, so a
  // fresh-per-render transform would re-key this and reintroduce #5567.
  const resolvedData = useMemo<TData[]>(() => {
    if (source === undefined || flatten === undefined) {
      return data ?? EMPTY_ROWS
    }
    const flat = flatten(source)

    return dedupeBy === undefined ? flat : dedupeRows(flat, dedupeBy)
  }, [source, flatten, dedupeBy, data])

  // Every interactive engine-state slice shares one seam (`useEngineStateSlice`): the affordance
  // is inert unless the slice is controlled-with-handler or wrapper-owned, so the wrapper owns it
  // on the uncontrolled path (seeded from any `state.X` initial value). `manualSorting` (server
  // mode) forces sorting controlled even without a local handler. `columnSizingInfo` is the
  // transient drag state paired with `columnSizing`; it has no consumer handler, so it is ALWAYS
  // wrapper-owned — a live resize tracks even when the consumer lifts `columnSizing` out.
  const sorting = useEngineStateSlice(
    state?.sorting,
    onSortingChange,
    EMPTY_SORTING,
    manualSorting === true,
  )
  const pinning = useEngineStateSlice(
    state?.columnPinning,
    onColumnPinningChange,
    EMPTY_COLUMN_PINNING,
  )
  const sizing = useEngineStateSlice(state?.columnSizing, onColumnSizingChange, EMPTY_COLUMN_SIZING)
  const sizingInfo = useEngineStateSlice<ColumnSizingInfoState>(
    state?.columnSizingInfo,
    undefined,
    DEFAULT_COLUMN_SIZING_INFO,
  )
  const ordering = useEngineStateSlice(state?.columnOrder, onColumnOrderChange, EMPTY_COLUMN_ORDER)

  // Splice each wrapper-owned slice in only on its uncontrolled path; a controlled slice
  // keeps its value from `state`. Memoized for a stable identity except when an internal
  // slice changes. Seed values are TanStack defaults, so an unconfigured table is unchanged.
  const engineState = useMemo<Partial<TableState> | undefined>(
    () => ({
      ...state,
      ...(sorting.isControlled ? {} : { sorting: sorting.internalValue }),
      ...(pinning.isControlled ? {} : { columnPinning: pinning.internalValue }),
      ...(sizing.isControlled ? {} : { columnSizing: sizing.internalValue }),
      ...(sizingInfo.isControlled ? {} : { columnSizingInfo: sizingInfo.internalValue }),
      ...(ordering.isControlled ? {} : { columnOrder: ordering.internalValue }),
    }),
    [
      state,
      sorting.isControlled,
      sorting.internalValue,
      pinning.isControlled,
      pinning.internalValue,
      sizing.isControlled,
      sizing.internalValue,
      sizingInfo.isControlled,
      sizingInfo.internalValue,
      ordering.isControlled,
      ordering.internalValue,
    ],
  )

  const table = useReactTable<TData>({
    data: resolvedData,
    columns,
    state: engineState,
    enableSorting,
    manualSorting,
    // MUI parity: resizing on by default (matches TanStack's own default), opt a column out via
    // `columnDef.enableResizing: false`. `getCanResize()` then gates the U4 handle per column.
    enableColumnResizing: enableColumnResizing ?? true,
    columnResizeMode,
    // Pinning is OPT-IN (opposite TanStack's on-default): off unless a consumer asks, so a
    // plain sortable table stays menu-free (`getCanPin()` gates the header menu's Pin items).
    enableColumnPinning: enableColumnPinning ?? false,
    getRowId,
    onSortingChange: sorting.engineOnChange,
    onColumnPinningChange: pinning.engineOnChange,
    onColumnSizingChange: sizing.engineOnChange,
    onColumnSizingInfoChange: sizingInfo.engineOnChange,
    onColumnOrderChange: ordering.engineOnChange,
    getCoreRowModel: coreRowModel as RowModelFactory<TData>,
    getSortedRowModel: sortedRowModel as RowModelFactory<TData>,
    ...tableOptions,
  })

  const rows = table.getRowModel().rows

  const scrollRef = useRef<HTMLDivElement>(null)
  // Memoized so the virtualizer sees a stable identity and doesn't re-measure every
  // render; a consumer override is used as-is and should itself be stable.
  const defaultGetScrollElement = useMemo<() => HTMLElement | null>(
    () => () => scrollRef.current,
    [],
  )
  // Per-row slot = density row height + the inter-row gap. Both legs read the same
  // `DATA_TABLE_ROW_HEIGHT` (TableRow's inline height and here), so a windowed row leaves
  // exactly the gap with no CSS literal to drift against. `rowGapPx` defaults to the 1px
  // floating-row gap; the bordered-row preset passes `0` so divider rows sit contiguous.
  const defaultEstimateSize = useMemo<(index: number) => number>(
    () => () => DATA_TABLE_ROW_HEIGHT[density] + rowGapPx,
    [density, rowGapPx],
  )
  const virtualizer = useVirtualizer<HTMLElement, Element>({
    count: virtual?.count ?? rows.length,
    getScrollElement: virtual?.getScrollElement ?? defaultGetScrollElement,
    estimateSize: virtual?.estimateSize ?? defaultEstimateSize,
    overscan: virtual?.overscan ?? DEFAULT_OVERSCAN,
    // Default 0 keeps the hook layout-agnostic; the DataTable preset sets both to the
    // sticky header height so `scrollToIndex` reaches the last rows (see option docs).
    scrollMargin: virtual?.scrollMargin ?? 0,
    scrollPaddingStart: virtual?.scrollPaddingStart ?? 0,
  })

  return {
    table,
    virtualizer,
    rows,
    scrollRef,
    density,
    sortingState: table.getState().sorting,
    columnSizingState: table.getState().columnSizing,
    isAnyColumnResizing: table.getState().columnSizingInfo.isResizingColumn !== false,
    columnPinningState: table.getState().columnPinning,
    columnOrderState: table.getState().columnOrder,
    columnVisibilityState: table.getState().columnVisibility,
  }
}
